
import { memo, useCallback, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  FormGroup,
  FormHelperText,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import TextField from '../../../../bmr/components/styled/FormField'
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Switch from '../../../../bmr/components/styled/Switch';
import Select from '../../../../bmr/components/styled/SelectField';

//@info using the placeholder text component to avoid code repetition just in file.
const PlaceholderText = ({ placeholder }) => (
  <span style={{ color: '#9e9e9e' }}>{placeholder}</span>
);

const FormField = memo(({
  name,
  type = 'text',
  label = '',
  placeholder,
  options = [],
  fullWidth = true,
  rows = 4,
  disabled = false,
  row = '', //@info this row is using for the horziontal radio input.
  description = '',
  inputLabelTop = '',
  rules = {},
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const renderField = useCallback((field, fieldState) => {
    const { error, isTouched, isDirty } = fieldState;
    const hasError = !!error && (isTouched || isDirty);

    switch (type) {
      case 'text':
      case 'email':
        return (
            <TextField             
              {...field}
              type={type}
              placeholder={placeholder}
              fullWidth={fullWidth}
              error={hasError}
              helperText={error?.message}
              disabled={disabled}
            InputProps={{
              classes: {
                input: props.className,
              },
            }}
              {...props}
                  sx={{
                ...error && {
                  '& .MuiInputBase-input': {
                        border: '1px solid #ff2023'
                  }
                }
              }}
            />
        );

      case 'number':
        return (
          <TextField
            {...field}
            type="number"
            label={label}
            placeholder={placeholder}
            fullWidth={fullWidth}
            error={hasError}
            helperText={error?.message}
            disabled={disabled}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : Number(e.target.value);
              field.onChange(value);
            }}
            {...props}
          />
        );

      case 'password':
        return (
          <TextField
            {...field}
            type={showPassword ? 'text' : 'password'}
            label={label}
            placeholder={placeholder}
            fullWidth={fullWidth}
            error={hasError}
            helperText={error?.message}
            disabled={disabled}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePassword}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...props}
          />
        );

      case 'textarea':
        return (
            <TextField
              {...field}
              label={label}
              placeholder={placeholder}
              fullWidth={fullWidth}
              multiline
              rows={rows}
              className='mb-3'
              size="small"
              error={hasError}
              helperText={error?.message}
              disabled={disabled}
              {...props}
            />
        );

      case 'select':
        return (
          <FormControl fullWidth={fullWidth} error={hasError} disabled={disabled} className='controlledSelect'>
            <Select
              {...field}
              displayEmpty
              label={label}
              name={label}
              value={props?.multiple ? (field?.value || []) : (field?.value ?? '')}
              {...props}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                PaperProps: {
                  style: {
                    minWidth: '12.5rem',
                  },
                },
                disablePortal: true,
              }}
              renderValue={(selected) => {
                if (!selected || selected?.length === 0) {
                  return <PlaceholderText placeholder={placeholder} />;
                }

                if (props.multiple) {
                  return <PlaceholderText placeholder={placeholder} />;
                }

                const selectedOption = options?.find(opt => opt?.value === selected);
                return selectedOption?.label || selected;
              }}
            >
              <MenuItem disabled value="">
                <em>{placeholder}</em>
              </MenuItem>
              {options?.map((option) => (
                <MenuItem key={option?.value} value={option?.value} disabled={option?.disabled}>
                  {option?.label}
                </MenuItem>
              ))}
            </Select>
            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );

      case 'switch':
        return (
          <FormControl error={hasError} disabled={disabled} >

            <FormControlLabel
              className='font-medium text-sm ml-0'
              control={
                <Switch
                  {...field}
                  checked={!!field.value}
                  {...props}
                />
              }
              label={label}
            />
            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );
      case 'checkbox-group':
        return (
          <FormControl error={hasError} disabled={disabled} component="fieldset">
            <FormLabel component="legend">{label}</FormLabel>
            <FormGroup>
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={field.value?.includes(option.value) || false}
                      onChange={(e) => {
                        const currentValue = field.value || [];
                        if (e.target.checked) {
                          field.onChange([...currentValue, option.value]);
                        } else {
                          field.onChange(currentValue.filter(v => v !== option.value));
                        }
                      }}
                      {...props}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );

      case 'radio-group':
        return (
           <FormControl
            error={hasError}
            disabled={disabled}
            component="fieldset"
            sx={{ display: "block" }}
          >
            {label && <FormLabel component="legend" className="text-xs">
              {label}
            </FormLabel>
            }
            <RadioGroup
              row={row}
              value={field.value ?? ""}
              onChange={(e) => {
                field.onChange(e.target.value);
                props.onChange?.(e);
              }}
              className='h-8'
            >
              {options.map((option) => (
                <FormControlLabel
                  key={String(option.value)}
                  value={option.value}
                  control={<Radio size="small" color="primary" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl> 
        );

      case 'file':
        return (
          <FormControl fullWidth={fullWidth} error={hasError} disabled={disabled}>
            <input
              type="file"
              id={name}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                field.onChange(file);
              }}
              {...props}
            />
            <label htmlFor={name}>
              <Button
                variant="outlined"
                component="span"
                fullWidth={fullWidth}
                disabled={disabled}
              >
                {field.value?.name || label || 'Choose File'}
              </Button>
            </label>
            {hasError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        );

      default:
        return (
          <TextField
            {...field}
            type={type}
            label={label}
            placeholder={placeholder}
            fullWidth={fullWidth}
            error={hasError}
            helperText={error?.message}
            disabled={disabled}
            {...props}
          />
        );
    }
  }, [type, label, placeholder, fullWidth, rows, disabled, options, showPassword, handleTogglePassword, name, props]);
  
  return (
    <Controller
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
       <Box
  sx={{
    marginBottom: props.noMargin
      ? 0
      : props.size === 'small'
      ? '.5rem'
      : '1rem'
  }}
>

         {inputLabelTop && <Typography variant='p' className='customLabel inline-block'>{inputLabelTop}</Typography>}
          {renderField(field, fieldState)}
          {description && (
            <Box>
            <Typography color={"darkgray"} variant="caption" className='mb-2 text-xs text-[#828282]'  >
              {description}
            </Typography>
            </Box>
          )}
        </Box>
      )}
    />
  );
});

FormField.displayName = 'FormField';

export default FormField;